import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useUserStats } from '../context/UserStatsContext';
import { Button, AudioPlayer, FeedbackModal, PracticeCompleteModal, PracticeTimer, LoadingWhale } from '../components/common';
import { checkGrammar, getGrammarExercise } from '../services/api';
import { GrammarCheckResult, SessionResult } from '../types';

// Fallback exercises
const fallbackExercises = {
  basic: [
    'The cat is big.',
    'I like apples.',
    'She has a red ball.',
    'The dog runs fast.',
    'My mom is nice.',
  ],
  intermediate: [
    'The children are playing in the park.',
    'Yesterday I went to the library.',
    'My sister likes to read books.',
    'The weather is beautiful today.',
    'We are going to the beach tomorrow.',
  ],
  advanced: [
    'The magnificent castle stood on top of the hill.',
    'Although it was raining, we decided to go hiking.',
    'The scientist discovered a new species of butterfly.',
    'My grandmother taught me how to bake cookies.',
    'The orchestra performed a beautiful symphony last night.',
  ],
};

const GrammarPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useUser();
  const { startSession, endSession, stats, loading: statsLoading } = useUserStats();

  const [sentence, setSentence] = useState<string>('');
  const [userInput, setUserInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [result, setResult] = useState<GrammarCheckResult | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [hasListened, setHasListened] = useState(false);
  const [exerciseCount, setExerciseCount] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [sessionResult, setSessionResult] = useState<SessionResult | null>(null);
  const [isPracticing, setIsPracticing] = useState(true);

  const complexity = profile?.complexity || 'basic';

  // Start session when stats are loaded
  useEffect(() => {
    if (stats && !statsLoading) {
      startSession('grammar');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, statsLoading]);

  const loadExercise = async () => {
    setIsLoading(true);
    setResult(null);
    setUserInput('');
    setHasListened(false);
    setHasSubmitted(false);

    try {
      const data = await getGrammarExercise(complexity);
      setSentence(data.sentence);
    } catch (err) {
      // Use fallback
      const exercises = fallbackExercises[complexity];
      const randomIndex = Math.floor(Math.random() * exercises.length);
      setSentence(exercises[randomIndex]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadExercise();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexity]);

  const handleCheck = async () => {
    if (!userInput.trim() || !sentence || hasSubmitted) return;

    setIsChecking(true);
    setHasSubmitted(true);

    try {
      const checkResult = await checkGrammar(userInput, sentence, complexity);
      setResult(checkResult);
      setShowFeedback(true);
      setExerciseCount((prev) => prev + 1);
    } catch (err) {
      // Simple local check as fallback (ignore punctuation)
      const normalize = (text: string) => text.trim().toLowerCase().replace(/[.,!?;:'"()-]/g, '');
      const isCorrect = normalize(userInput) === normalize(sentence);
      setResult({
        isCorrect,
        errors: isCorrect ? [] : [{ word: userInput, suggestion: sentence, type: 'spelling' }],
        score: isCorrect ? 100 : 50,
        feedback: isCorrect ? 'Perfect!' : 'Almost! Check the words carefully.',
      });
      setShowFeedback(true);
      setExerciseCount((prev) => prev + 1);
    } finally {
      setIsChecking(false);
    }
  };

  const handleNext = () => {
    setShowFeedback(false);
    loadExercise();
  };

  const handleTryAgain = () => {
    setShowFeedback(false);
    setResult(null);
    setUserInput('');
    setHasSubmitted(false);
    // Keep the same sentence, just reset input
  };

  const handleAudioPlay = () => {
    setHasListened(true);
  };

  const handleGoBack = async () => {
    console.log('[DEBUG GrammarPage] handleGoBack called');
    setIsPracticing(false);
    // End session and show progress modal
    const avgScore = result?.score || 0;
    console.log('[DEBUG GrammarPage] Calling endSession with score:', avgScore);
    const sessionRes = await endSession(avgScore);
    console.log('[DEBUG GrammarPage] endSession result:', sessionRes);
    if (sessionRes) {
      setSessionResult(sessionRes);
      setShowProgressModal(true);
    } else {
      console.log('[DEBUG GrammarPage] No sessionRes, navigating to /app');
      navigate('/app');
    }
  };

  const handleContinuePractice = () => {
    setShowProgressModal(false);
    setSessionResult(null);
    setIsPracticing(true);
    // Start a new session
    startSession('grammar');
  };

  const handleGoHome = () => {
    setShowProgressModal(false);
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900">
      {/* Header - Always rendered to keep timer running */}
      <header className="bg-white dark:bg-slate-800 shadow-md dark:shadow-slate-900 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
          >
            <span className="text-2xl">{'\u2190'}</span>
            <span className="font-bold">Done</span>
          </button>
          <h1 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
            <span>{'\u{1F4DD}'}</span> Grammar
          </h1>
          <PracticeTimer isActive={isPracticing} />
        </div>
      </header>

      {/* Loading state */}
      {isLoading ? (
        <LoadingWhale message="Loading exercise..." />
      ) : (
      /* Main content */
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Exercise counter */}
        <div className="text-center mb-4">
          <span className="text-gray-500 dark:text-gray-400">Exercise {exerciseCount + 1}</span>
        </div>

        {/* Instructions */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900 mb-6">
          <div className="text-center mb-6">
            <span className="text-5xl mb-4 block">{'\u{1F3A7}'}</span>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Listen and Type</h2>
            <p className="text-gray-600 dark:text-gray-300">Listen to the sentence, then type what you hear!</p>
          </div>

          {/* Audio player */}
          <div className="flex justify-center">
            <AudioPlayer
              text={sentence}
              maxPlays={3}
              onPlay={handleAudioPlay}
              size="large"
            />
          </div>
        </div>

        {/* Input section */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900">
          <label className="block text-gray-700 dark:text-gray-200 font-bold mb-4 text-center text-lg">
            Type the sentence you heard:
          </label>

          <textarea
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              setHasSubmitted(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (userInput.trim() && hasListened && !isChecking && !hasSubmitted) {
                  handleCheck();
                }
              }
            }}
            placeholder={hasListened ? 'Type the sentence here... (Press Enter to check)' : 'Listen to the audio first!'}
            disabled={!hasListened}
            className={`
              w-full px-4 py-4 rounded-2xl border-4 text-xl text-gray-800 dark:text-white
              focus:outline-none transition-all duration-200
              ${hasListened
                ? 'border-indigo-300 dark:border-indigo-600 focus:border-indigo-500 dark:focus:border-indigo-400 bg-white dark:bg-slate-700'
                : 'border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 cursor-not-allowed'
              }
            `}
            rows={3}
          />

          <div className="mt-6">
            <Button
              onClick={handleCheck}
              variant="primary"
              size="large"
              fullWidth
              disabled={!userInput.trim() || isChecking || !hasListened || hasSubmitted}
            >
              {isChecking ? 'Checking...' : 'Check My Answer'}
            </Button>
          </div>
        </div>

        {/* Skip button */}
        <div className="text-center mt-6">
          <Button onClick={handleNext} variant="secondary" size="medium">
            Skip this one
          </Button>
        </div>
      </main>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={result?.isCorrect ? 'success' : 'error'}
        title={result?.isCorrect ? 'Perfect!' : 'Almost!'}
        message={result?.feedback || 'Keep practicing!'}
        score={result?.score}
        buttonText="Next Exercise"
        onButtonClick={handleNext}
        userAnswer={!result?.isCorrect ? userInput : undefined}
        correctAnswer={!result?.isCorrect ? sentence : undefined}
        onTryAgain={handleTryAgain}
      />

      {/* Practice Complete Modal */}
      <PracticeCompleteModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        result={sessionResult}
        onContinue={handleContinuePractice}
        onGoHome={handleGoHome}
      />
    </div>
  );
};

export default GrammarPage;
